import React, { useState, useRef, useMemo, useEffect } from "react";

import { Box, Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  RowActionsExtra,
  Constants as GridConstants,
  SearchSuggest,
  ColumnFilter,
  Column,
  GeneralActions,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import classnames from "classnames";
import { formatInTimeZone } from "date-fns-tz";
import { Gallery, Warning2 } from "iconsax-react";
import lodash from "lodash";
import { useTranslation } from "react-i18next";
import { AiOutlineCamera, AiOutlineCopy, AiOutlinePlus } from "react-icons/ai";
import { CgFileRemove } from "react-icons/cg";
import { BiCommentDetail, BiImages } from "react-icons/bi";
import { FiEdit } from "react-icons/fi";
import { MdOutlineChangeCircle } from "react-icons/md";
import { v4 as uuid } from "uuid";

import { Step } from "Context/ActualInventoryContext";
import { useAppState } from "Context/StateContext";
import { useActualInventoryContext } from "Hooks/useActualInventoryContext";
import { ItemException } from "Models/item.exception";
import { ItemTypes } from "Models/item.types";
import { Room } from "Models/room";
import { ItemJob } from "Models/sales";
import { SearchAttribute } from "Models/search.attribute";
import { Filters } from "Models/shipeezi.table";
import { StorageBox } from "Models/storage.box";
import { Symbol } from "Models/symbol";
import { convertEventToFile } from "Util/convertEventToFile";
import { handleGetConvertValue, Metric } from "Util/converts";
import {
  ConditionFilter,
  ItemCharacteristicsFilter,
  ItemJobStatus,
  ItemJobSubStatus,
  ItemProGearBelongsTo,
  JobStatus,
  MEChecklistFilter,
  PhotosFilter,
  QuestionBooleanType,
} from "Util/enums";
import { filter } from "Util/filter";
import { filterSearch } from "Util/filterSearch";
import { formatItemConditions } from "Util/formatItemConditions";
import { formatItemLocation } from "Util/formatItemLocation";
import { formatItemPacker } from "Util/formatItemPacker";
import { getMeasurement } from "Util/getMeasurement";
import { getNextNumberOfItemsJob } from "Util/getNextNumberOfItemsJob";
import { hasOnlyNumbers } from "Util/hasOnlyNumbers";
import { onSearch } from "Util/onSearch";
import { paginate } from "Util/paginate";
import CheckItemJobNumberME from "Components/CheckItemJobNumberME";
import Button, { Color } from "Components/Form/Button";
import BadgeItemJobStatus from "Components/Generic/BadgeItemJobStatus";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import ChangeItemStatusModal from "../ChangeItemStatusModal";
import CreateActualItemModal from "../CreateActualItemModal";
import { CreateActualItemPdfModal, PropsHandler } from "../CreateActualItemPDFModal/index";
import { BingoSheetFormType } from "../CreateActualItemPDFModal/BingoSheet";
import DrawerComments from "../DrawerComments";
import DrawerPhotos from "../DrawerPhotos";
import useStyles, {
  PicturesCount,
  PicturesIconWrapper,
  CreateInventoryButton,
  CreateInventoryButtonLabel,
  ViewInventoryButton,
} from "./styles";
import IconFolderOpen from "Components/Generic/Icons/IconFolderOpen";
import { JobInventoryPDF } from "Models/job.inventory.pdf";
import { DrawerPDF, DrawerPropsHandler } from "../DrawerInventoryPdf";
import ConfirmationModal from "Components/Dialogs/ConfirmationModal";

const hardwareBoxIcon = require("Assets/img/hardwareBox-icon.svg");
const highRiskIcon = require("Assets/img/highRisk-icon.svg");
const meIcon = require("Assets/img/me-icon.svg");
const proGearIcon = require("Assets/img/proGear-icon.svg");
const commentsIcon = require("Assets/img/comments.svg");

/** Stable id for matching rows across the manual bingo batch replace flow. */
const itemJobStableKey = (ij: ItemJob): string => {
  const u = ij.uuid?.trim();
  if (u) return u;
  if (ij.id != null) return `id:${ij.id}`;
  return "";
};

export interface ActualInventoryProps {
  itemsJobs: ItemJob[];
  isOnViewMode?: boolean;
  orderStatus?: string;
  storageBoxes?: StorageBox[];
  update: (itemsJobs: ItemJob[]) => void;
  packingTypes?: any[];
  rooms?: any[];
  methodsOfPreparation?: any[];
  statuses?: any[];
  uniqueLocations?: string[];
  onFiltersChange?: (filters: any) => void;
  loadingData?: boolean;
  inventoryPdf?: JobInventoryPDF | null;
  updatePdfFile: (value: JobInventoryPDF | null) => void;
}

const ActualInventoryList = (props: ActualInventoryProps) => {
  const {
    itemsJobs,
    isOnViewMode,
    orderStatus,
    storageBoxes = [],
    update,
    uniqueLocations = [],
    onFiltersChange,
    loadingData = false,
    inventoryPdf,
    updatePdfFile,
  } = props;

  const isDeliveredOrder =
    orderStatus?.toLowerCase() === JobStatus.DELIVERED.toLowerCase();
  const inventoryReadOnly = !!isOnViewMode || isDeliveredOrder;

  const measurement = getMeasurement();

  const {
    itemStatus,
    uuidItemJob,
    setUUIDItemJob,
    setItemJob,
    setCurrentStep,
    setStepsOption,
    setErrors,
    rooms,
    itemsType,
    descriptiveSymbols,
  } = useActualInventoryContext();

  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const photoRef = useRef<HTMLInputElement>(null);
  const createInventoryModalRef = useRef<PropsHandler>(null);
  const drawerPdfRef = useRef<DrawerPropsHandler>(null);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openChangeStatusModal, setOpenChangeStatusModal] = useState<boolean>(false);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const [openDrawerComments, setOpenDrawerComments] = useState<boolean>(false);
  const [openDrawerPhotos, setOpenDrawerPhotos] = useState<boolean>(false);

  const [filters, setFilters] = useState<Filters>();

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(100);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const [uuidUpload, setUUIDUpload] = useState<string>();

  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [openOnlyComments, setOpenOnlyComments] = useState(false);
  const [openRemoveInventoryPdfConfirm, setOpenRemoveInventoryPdfConfirm] = useState(false);

  const [mustBeHardwareBox, setMustBeHardwareBox] = useState<boolean>(
    currentUser?.company.autoHardwareBox || false
  );

  const [persistedManualInventoryForm, setPersistedManualInventoryForm] =
    useState<BingoSheetFormType | null>(null);

  /** Keys (uuid or id:*) of rows last created/updated via the manual bingo modal — replaced on next submit. */
  const [manualBatchItemKeys, setManualBatchItemKeys] = useState<string[]>([]);

  useEffect(() => {
    if (itemsJobs.length === 0) {
      setPersistedManualInventoryForm(null);
      setManualBatchItemKeys([]);
    }
  }, [itemsJobs.length]);

  useEffect(() => {
    const keysInGrid = new Set(itemsJobs.map(itemJobStableKey).filter(Boolean));
    setManualBatchItemKeys((prev) => prev.filter((k) => keysInGrid.has(k)));
  }, [itemsJobs]);

  useEffect(() => {
    if (manualBatchItemKeys.length === 0) {
      setPersistedManualInventoryForm(null);
    }
  }, [manualBatchItemKeys.length]);

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    if (filtersKeys && filtersKeys?.length) {
      setFilters(value);

      if (onFiltersChange) {
        const backendFilters = convertFiltersToBackendFormat(value);
        onFiltersChange(backendFilters);
      }
    } else {
      setFilters(undefined);

      if (onFiltersChange) {
        onFiltersChange(undefined);
      }
    }
  };

  const enumToArray = <T extends Record<string, string>>(enumObject: T): string[] => {
    return Object.values(enumObject);
  };

  const convertFiltersToBackendFormat = (filters: any) => {
    const backendFilters: any = {};

    if (filters.itemCharacteristics?.length) {
      backendFilters.itemCharacteristics = filters.itemCharacteristics;
    }

    if (filters.packingType?.length) {
      const packingTypeIds = filters.packingType
        .map((typeName: string) => {
          const typeFound = itemsType?.find((type) => type?.name === typeName);
          return typeFound?.id;
        })
        .filter(Boolean);

      if (packingTypeIds.length > 0) {
        backendFilters.itemTypeIds = packingTypeIds;
      }
    }

    if (filters.room?.length) {
      const roomIds = filters.room
        .map((roomName: string) => {
          const roomFound = rooms?.find((room) => room?.name === roomName);
          return roomFound?.id;
        })
        .filter(Boolean);

      if (roomIds.length > 0) {
        backendFilters.roomIds = roomIds;
      }
    }

    if (filters.methodOfPreparation?.length) {
      const symbolIds = filters.methodOfPreparation
        .map((symbolName: string) => {
          const symbolFound = descriptiveSymbols?.find((symbol) => symbol?.name === symbolName);
          return symbolFound?.id;
        })
        .filter(Boolean);

      if (symbolIds.length > 0) {
        backendFilters.symbolIds = symbolIds;
      }
    }

    if (filters.status?.length) {
      backendFilters.statuses = filters.status;
    }

    if (filters.location?.length) {
      backendFilters.locations = filters.location;
    }

    if (filters.photos?.length) {
      backendFilters.hasPhotos = filters.photos.includes(PhotosFilter.HAS_PHOTOS)
        ? true
        : filters.photos.includes(PhotosFilter.NO_PHOTOS)
        ? false
        : undefined;
    }

    if (filters.condition?.length) {
      if (filters.condition.includes(ConditionFilter.HAS_CONDITION)) {
        backendFilters.hasCondition = true;
      }
      if (filters.condition.includes(ConditionFilter.NO_CONDITION)) {
        backendFilters.hasCondition = false;
      }
    }

    if (filters.meChecklist?.length) {
      backendFilters.meDone = filters.meChecklist.includes(MEChecklistFilter.DONE)
        ? true
        : filters.meChecklist.includes(MEChecklistFilter.NOT_DONE)
        ? false
        : undefined;
    }

    return backendFilters;
  };

  const sortItemsJobs = (a: ItemJob, b: ItemJob) => {
    const hardwareBoxSort = Number(b.item?.isHardwareBox || 0) - Number(a.item?.isHardwareBox || 0);

    return hardwareBoxSort || Number(a.number || 0) - Number(b.number || 0);
  };

  const filterSearchItems = (items: ItemJob[], searchAttributes: SearchAttribute[]): ItemJob[] => {
    return searchAttributes.reduce((filteredItems, searchAttribute) => {
      const { attribute, values } = searchAttribute;

      return filteredItems.filter((item: ItemJob) => {
        return values.some((value) => {
          const searchValue = value.toLowerCase();

          switch (attribute) {
            case "description":
              return String(item.description || "")
                .toLowerCase()
                .includes(searchValue);

            case "number":
              return String(item.number || "")
                .toLowerCase()
                .includes(searchValue);

            case "itemType":
              return String(item.itemType?.name || "")
                .toLowerCase()
                .includes(searchValue);

            case "packer":
              return String(item.packer || "")
                .toLowerCase()
                .includes(searchValue);

            case "room":
              return String(item.room?.name || "")
                .toLowerCase()
                .includes(searchValue);

            case "status":
              return String(item.status || "")
                .toLowerCase()
                .includes(searchValue);

            default:
              return false;
          }
        });
      });
    }, items);
  };

  const itemsJobsPaginate = useMemo(() => {
    let data = itemsJobs;

    const savedItems = data.filter((item) => item.id);
    const newItems = data.filter((item) => !item.id && item.uuid);

    let filteredSavedItems = savedItems;
    let filteredNewItems = newItems;

    if (searchAttributes.length > 0) {
      filteredSavedItems = filterSearchItems(filteredSavedItems, searchAttributes);
      filteredNewItems = filterSearchItems(filteredNewItems, searchAttributes);
    }

    if (filters && !onFiltersChange) {
      filteredSavedItems = filter(filteredSavedItems, filters);
      filteredNewItems = filter(filteredNewItems, filters);
    }

    const combinedData = [...filteredSavedItems, ...filteredNewItems];

    return searchAttributes.length > 0 || filters
      ? combinedData
      : paginate(combinedData, page > 0 ? page - 1 : 0, pageSize);
  }, [itemsJobs, page, pageSize, searchAttributes, filters, onFiltersChange]);

  const calculateTotalItems = (
    itemsJobs: ItemJob[],
    searchAttributes: SearchAttribute[],
    filters: Filters | undefined,
    onFiltersChange?: (filters: any) => void
  ): number => {
    const savedItems = itemsJobs.filter((item) => item.id);
    const newItems = itemsJobs.filter((item) => !item.id && item.uuid);

    if (searchAttributes?.length > 0) {
      const filteredSaved = filterSearchItems(savedItems, searchAttributes);
      const filteredNew = filterSearchItems(newItems, searchAttributes);
      const total = filteredSaved.length + filteredNew.length;

      return total;
    }

    if (filters && !onFiltersChange) {
      const filteredSaved = filter(savedItems, filters);
      const filteredNew = filter(newItems, filters);
      const total = filteredSaved.length + filteredNew.length;

      return total;
    }

    return itemsJobs.length;
  };

  const cloneItemJob = (itemJob: ItemJob) => {
    if (itemJob) {
      const itemJobCloned = lodash.cloneDeep(itemJob);

      if (itemJobCloned?.id) delete itemJobCloned.id;
      if (itemJobCloned?.createdAt) delete itemJobCloned.createdAt;
      if (itemJobCloned?.updatedAt) delete itemJobCloned.updatedAt;
      if (itemJobCloned?.picturesDeleted?.length) delete itemJobCloned.picturesDeleted;

      if (itemJobCloned && itemJobCloned?.exceptions?.length) {
        itemJobCloned?.exceptions?.map((exception) => {
          if (exception?.id) delete exception.id;
          if (exception?.createdAt) delete exception.createdAt;
          if (exception?.updatedAt) delete exception.updatedAt;
          if (exception?.itemJobId) delete exception.itemJobId;

          return exception;
        });
      }

      if (itemJobCloned && itemJobCloned?.pictures?.length) {
        itemJobCloned?.pictures?.map((picture) => {
          if (picture?.id) delete picture.id;
          if (picture?.createdAt) delete picture.createdAt;
          if (picture?.updatedAt) delete picture.updatedAt;
          if (picture?.itemJobId) delete picture.itemJobId;
          if (picture?.name) delete picture.name;

          return picture;
        });
      }

      const itemJobUpdated = {
        ...(itemJobCloned || {}),
        number: getNextNumberOfItemsJob(itemsJobs),
        uuid: uuid(),
      };

      update([...(itemsJobs || []), itemJobUpdated]);
    }
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit",
      buttonAndAction: (_, itemJob: ItemJob) => {
        return (
          <Tooltip title={translate("edit") || ""} placement="top">
            <div>
              <FiEdit
                className={classes.iconExtraAction}
                size={GridConstants.defaultRowActionSize + 1}
                color={GridConstants.defaultColorIcon}
                onClick={() => {
                  setOpenOnlyComments(false);
                  setItemJob({
                    ...itemJob,
                    number: itemJob?.number || getNextNumberOfItemsJob(itemsJobs),
                  });
                  setUUIDItemJob(itemJob?.id?.toString() || itemJob?.uuid);
                  setOpenModal(true);

                  setMustBeHardwareBox(
                    !!currentUser?.company?.autoHardwareBox &&
                      !!itemJob?.isHardwareBox &&
                      itemsJobs.filter((i) => i.isHardwareBox && i.id !== itemJob.id)?.length === 0
                  );
                }}
                data-cy="editActualItem"
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: translate("duplicate"),
      key: "copy",
      buttonAndAction: (_, itemJob: ItemJob) => (
        <Tooltip title={translate("duplicate") || ""} placement="top">
          <div>
            <AiOutlineCopy
              className={classes.iconExtraAction}
              size={GridConstants.defaultRowActionSize + 1}
              color={GridConstants.defaultColorIcon}
              onClick={() => {
                cloneItemJob(itemJob);
              }}
              data-cy="duplicateActualItem"
            />
          </div>
        </Tooltip>
      ),
    },
    {
      title: translate("upload"),
      key: "upload",
      buttonAndAction: (_, itemJob: ItemJob) => (
        <Tooltip title={translate("upload") || ""} placement="top">
          <div>
            <AiOutlineCamera
              className={classes.iconExtraAction}
              size={GridConstants.defaultRowActionSize + 2}
              color={GridConstants.defaultColorIcon}
              onClick={() => {
                photoRef?.current?.click();
                setUUIDUpload(itemJob?.id?.toString() || itemJob?.uuid);
              }}
              data-cy="uploadPhotoActualItem"
            />
          </div>
        </Tooltip>
      ),
    },
  ];

  const rowActionsExtraView: RowActionsExtra[] = [
    {
      title: translate("comments"),
      key: "comments",
      buttonAndAction: (_, itemJob: ItemJob) => {
        if (itemJob && itemJob?.comments) {
          return (
            <Tooltip title={translate("comments") || ""} placement="top">
              <div>
                <BiCommentDetail
                  className={classes.iconExtraAction}
                  size={GridConstants.defaultRowActionSize + 1}
                  color={GridConstants.defaultColorIcon}
                  onClick={() => {
                    setOpenDrawerComments(true);
                    setItemJob(itemJob);
                  }}
                />
              </div>
            </Tooltip>
          );
        }

        return null;
      },
    },
    {
      title: translate("photos"),
      key: "photos",
      buttonAndAction: (_, itemJob: ItemJob) => {
        if (itemJob && itemJob?.pictures && itemJob?.pictures?.length > 0) {
          return (
            <Tooltip title={translate("photos") || ""} placement="top">
              <div>
                <BiImages
                  className={classes.iconExtraAction}
                  size={GridConstants.defaultRowActionSize + 1}
                  color={GridConstants.defaultColorIcon}
                  onClick={() => {
                    setOpenDrawerPhotos(true);
                    setItemJob(itemJob);
                  }}
                />
              </div>
            </Tooltip>
          );
        }

        return null;
      },
    },
  ];

  const handleCloseModal = () => {
    setItemJob(undefined);
    setUUIDItemJob(undefined);
    setOpenModal(false);
    setErrors([]);
    setCurrentStep(Step.DETAILS);
    setStepsOption([Step.DETAILS]);
    setOpenOnlyComments(false);
  };

  const renderButtonToChangeStatus = () => {
    if (selectedItems.length > 0) {
      let hasAJobWithTransshippedStatus: boolean = false;

      selectedItems.forEach((itemId) => {
        let itemJob;

        if (hasOnlyNumbers(itemId)) {
          itemJob = itemsJobs.find((itemJob) => itemJob.id === Number(itemId));
        } else {
          itemJob = itemsJobs.find((itemJob) => itemJob.uuid === itemId);
        }

        if (
          itemJob?.status &&
          [
            ItemJobStatus.TRANSSHIPPED.toLowerCase(),
            ItemJobStatus.NOT_SIGHTED.toLowerCase(),
          ].includes(itemJob?.status?.toLowerCase())
        ) {
          hasAJobWithTransshippedStatus = true;
        }
      });

      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          customColor={Color.INFO}
          data-cy="change-status"
          onClick={() => setOpenChangeStatusModal(true)}
          disabled={hasAJobWithTransshippedStatus}
        >
          <MdOutlineChangeCircle size={16} className="mr-2" />
          {translate("changeItemsStatus")}
        </Button>
      );
    }
  };

  const onSubmitManualInventoryBatch = (newBatch: ItemJob[]) => {
    const remove = new Set(manualBatchItemKeys.filter(Boolean));
    const withoutBatch = itemsJobs.filter((ij) => {
      const k = itemJobStableKey(ij);
      return !k || !remove.has(k);
    });
    update([...withoutBatch, ...newBatch]);
    setManualBatchItemKeys(newBatch.map(itemJobStableKey).filter(Boolean));
    createInventoryModalRef?.current?.closeModal();
  };

  const onSubmit = (itemJob: ItemJob) => {
    if (uuidItemJob !== undefined) {
      const itemsJobsUpdated = [...(itemsJobs || [])];

      const index = itemsJobsUpdated.findIndex(
        (itemJob) => itemJob?.id?.toString() === uuidItemJob || itemJob?.uuid === uuidItemJob
      );

      itemsJobsUpdated[index] = {
        ...itemsJobsUpdated[index],
        ...itemJob,
      };
      update(itemsJobsUpdated);
    } else {
      update([...(itemsJobs || []), itemJob]);
    }
  };

  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = await convertEventToFile(event, translate, ["jpg", "png", "jpeg"]);

    if (file && file?.uri) {
      const base64Splitted = file?.uri?.split(";base64,");

      const indexUpload = itemsJobs?.findIndex(
        (itemJob) => itemJob?.id?.toString() === uuidUpload || itemJob?.uuid === uuidUpload
      );

      if (
        base64Splitted &&
        base64Splitted?.length > 0 &&
        base64Splitted[1] &&
        indexUpload !== undefined
      ) {
        const itemsJobsUpdated = [...(itemsJobs || [])];

        let itemJobUpdated = itemsJobsUpdated[indexUpload];

        itemJobUpdated = {
          ...itemJobUpdated,
          pictures: [...(itemJobUpdated?.pictures || []), { mediaLink: base64Splitted[1] }],
        } as ItemJob;

        itemsJobsUpdated[indexUpload] = {
          ...itemsJobsUpdated[indexUpload],
          ...itemJobUpdated,
        };

        update(itemsJobsUpdated);
        NotificationToast.success(translate("photoAddedSuccessfully"));
      }
    }
    event.target.value = "";
  };

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);

    setSearchAttributes(attributes);
  };

  const checkIfMEISDone = (row: ItemJob) => {
    if (!row || (!row?.item?.mandatoryME && !row?.isMeCustom)) return false;
    if (row?.meDone?.toLowerCase()?.includes(QuestionBooleanType.YES?.toLowerCase())) {
      return !!row?.meWorking;
    }

    return !!row?.meDone;
  };

  const handleOpenComments = (row: ItemJob) => {
    setOpenOnlyComments(true);
    setItemJob({
      ...row,
      number: row?.number || getNextNumberOfItemsJob(itemsJobs),
    });
    setUUIDItemJob(row?.id?.toString() || row?.uuid);
    setOpenModal(true);
    setMustBeHardwareBox(
      !!currentUser?.company?.autoHardwareBox &&
        !!row?.isHardwareBox &&
        itemsJobs.filter((i) => i.isHardwareBox && i.id !== row.id)?.length === 0
    );
  };

  const columns: Column[] = [
    {
      name: "number",
      title: `${translate("item")}#`,
      hideSort: false,
      formatComponent: (value: string, row: ItemJob) => {
        if (checkIfMEISDone(row)) {
          return <CheckItemJobNumberME value={value} />;
        }

        return <span>{value}</span>;
      },
    },
    {
      name: "description",
      title: translate("description"),
      hideSort: true,
      formatComponent: (value: string, row: ItemJob) => {
        const descriptionTags = [];

        if (!row.id && row.uuid) {
          descriptionTags.push(
            <Tooltip placement="top" title={translate("unsavedItem")} key="unsaved">
              <div className={classnames(classes.displayFlexCenterDiv, classes.tag)}>
                <Warning2 size={18} color={"#FFA500"} />
              </div>
            </Tooltip>
          );
        }

        if (row.item?.isHardwareBox || row.isHardwareBox) {
          descriptionTags.push(
            <Tooltip placement="top" title={translate("hardwareBox")}>
              <div className={classnames(classes.displayFlexCenterDiv, classes.tag)}>
                <img className={classes.descriptionTags} src={hardwareBoxIcon} />
              </div>
            </Tooltip>
          );
        }

        if (row.item?.mandatoryME || row.isMeCustom) {
          descriptionTags.push(
            <Tooltip placement="top" title={translate("me")}>
              <div className={classnames(classes.displayFlexCenterDiv, classes.tag)}>
                <img className={classes.descriptionTags} src={meIcon} />
              </div>
            </Tooltip>
          );
        }

        if (row.item?.highRisk || row.isHighRisk) {
          descriptionTags.push(
            <Tooltip placement="top" title={translate("highRiskHighValue")}>
              <div className={classnames(classes.displayFlexCenterDiv, classes.tag)}>
                <img className={classes.descriptionTags} src={highRiskIcon} />
              </div>
            </Tooltip>
          );
        }

        if (row.item?.isProGear || row.isProGear) {
          descriptionTags.push(
            <Tooltip
              placement="top"
              title={`${translate("proGear")} ${
                row.proGearBelongsTo === ItemProGearBelongsTo.MEMBER
                  ? translate("member")
                  : translate("spouse")
              }`}
            >
              <div className={classnames(classes.displayFlexCenterDiv, classes.tag)}>
                <img className={classes.descriptionTags} src={proGearIcon} />
                <span>{row?.proGearBelongsTo?.charAt(0).toUpperCase()}</span>
              </div>
            </Tooltip>
          );
        }

        if (row.pictures && row.pictures?.length > 0) {
          descriptionTags.push(
            <Tooltip title={`${translate("photos")} ${row.pictures?.length}`} placement="top">
              <PicturesIconWrapper>
                <Gallery size="16" color={colors.brandBlue500} />
                {row.pictures && row.pictures?.length > 0 && (
                  <PicturesCount>{row.pictures.length}</PicturesCount>
                )}
              </PicturesIconWrapper>
            </Tooltip>
          );
        }

        if (row.comments && row.comments?.length > 0) {
          descriptionTags.push(
            <Tooltip title={`${translate("comments")}`} placement="top">
              <div
                className={classnames(classes.displayFlexCenterDiv, classes.tag)}
                onClick={() => {
                  !inventoryReadOnly && handleOpenComments(row);
                }}
                style={{ cursor: !inventoryReadOnly ? "pointer" : "default" }}
              >
                <img className={classes.descriptionTags} src={commentsIcon} />
              </div>
            </Tooltip>
          );
        }

        if (row.itemType?.isBox && !!row.itemJobBoxContents?.length) {
          const boxContentNames = row.itemJobBoxContents?.map((boxContent) => boxContent.name);
          const namesConcat = boxContentNames?.join(", ");
          const namesConcatSlice = namesConcat?.slice(0, 30)?.trim();
          const spread = namesConcat?.length > 30 ? "..." : "";

          if (namesConcat?.length > 30) {
            return (
              <Tooltip title={namesConcat} placement="top">
                <div className={classes.displayFlexCenterDiv}>
                  <span>{`${value} (${namesConcatSlice}${spread})`}</span>
                  {descriptionTags.map((tag) => tag)}
                </div>
              </Tooltip>
            );
          }

          return (
            <div className={classes.displayFlexCenterDiv}>
              <span>{`${value} (${namesConcatSlice})`}</span>
              {descriptionTags.map((tag) => tag)}
            </div>
          );
        }

        return (
          <div className={classes.displayFlexCenterDiv}>
            <span>{value}</span>
            {descriptionTags.map((tag) => tag)}
          </div>
        );
      },
    },
    {
      name: "itemType",
      title: translate("packingType"),
      formatText: (itemType: ItemTypes) => (itemType ? itemType?.name : ""),
      hideSort: true,
    },
    {
      name: "volume",
      title: translate("volume"),
      formatText: (volume: number) => {
        return `${handleGetConvertValue(
          volume || 0.0,
          measurement?.code,
          measurement?.decimalPlaces
        )} ${measurement?.code || Metric["m³"]}`;
      },
      hideSort: true,
    },
    {
      name: "packer",
      title: translate("packer"),
      formatText: (packer: string) => formatItemPacker(packer),
      hideSort: true,
    },
    {
      name: "room",
      title: translate("room"),
      formatText: (room: Room) => room?.name || "",
      hideSort: true,
    },
    {
      name: "symbol",
      title: translate("methodOfPreparation"),
      formatText: (symbol: Symbol) => (symbol ? symbol?.name : ""),
      hideSort: true,
    },
    {
      name: "exceptions",
      title: translate("conditionAtOrigin"),
      formatText: (exceptions: ItemException[]) => formatItemConditions(exceptions, false),
      hideSort: true,
    },
    {
      name: "exceptions",
      title: translate("conditionAtDestination"),
      formatText: (exceptions: ItemException[]) => formatItemConditions(exceptions, true),
      hideSort: true,
    },
    {
      name: "storageBoxes",
      title: translate("location"),
      formatComponent: (value: string, row: ItemJob) => formatItemLocation(row),
      hideSort: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => <BadgeItemJobStatus value={status} />,
      hideSort: true,
    },
    {
      name: "updatedAt",
      title: translate("lastUpdate"),
      hideSort: true,
      formatText: (updatedAt: string) => {
        return updatedAt
          ? formatInTimeZone(
              updatedAt,
              Intl.DateTimeFormat().resolvedOptions().timeZone,
              "dd/MM/yyyy hh:mm a"
            )
          : formatInTimeZone(
              new Date(),
              Intl.DateTimeFormat().resolvedOptions().timeZone,
              "dd/MM/yyyy hh:mm a"
            );
      },
    },
  ];

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[
          { name: "number", title: translate("item") + " #" },
          { name: "description", title: translate("description") },
          { name: "itemType", title: translate("packingType") },
          { name: "packer", title: translate("packer") },
          { name: "room", title: translate("room") },
          { name: "status", title: translate("status") },
        ]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={setSearchInput}
        placeholder="Search"
      />
    );
  };

  const renderCreateInventoryButton = () => {
    if (!!inventoryPdf) {
      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          flexWrap="nowrap"
          gap={1}
          sx={{ flexShrink: 0 }}
        >
          <Tooltip placement="top" title={translate("viewInventory")}>
            <ViewInventoryButton
              customColor={Color.BRAND_TEXT}
              startIcon={<IconFolderOpen width={20} height={20} color={colors.brandBlue500} />}
              onClick={() => drawerPdfRef?.current?.openModal()}
            >
              <span>{translate("viewInventory")}</span>
            </ViewInventoryButton>
          </Tooltip>
          {!isOnViewMode && !isDeliveredOrder && (
            <Button
              customColor={Color.ERROR_OUTLINED}
              startIcon={<CgFileRemove size={20} />}
              onClick={() => setOpenRemoveInventoryPdfConfirm(true)}
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                maxHeight: 40,
                "& .MuiButton-startIcon": { marginRight: "6px" },
              }}
            >
              <CreateInventoryButtonLabel>{translate("removeInventoryPdf")}</CreateInventoryButtonLabel>
            </Button>
          )}
        </Box>
      );
    }

    if (inventoryReadOnly) {
      return null;
    }

    const manualInventoryActionKey =
      manualBatchItemKeys.length > 0 ? "editInventory" : "createInventory";
    const isEditInventory = manualInventoryActionKey === "editInventory";

    return (
      <Tooltip placement="top" title={translate(manualInventoryActionKey)}>
        <CreateInventoryButton
          customColor={Color.BRAND_OUTLINED}
          startIcon={
            isEditInventory ? (
              <FiEdit color={colors.brandBlue500} size={20} />
            ) : (
              <AiOutlinePlus color={colors.brandBlue500} size={20} />
            )
          }
          onClick={() => createInventoryModalRef.current?.openModal(itemsJobs?.length || 0)}
        >
          <CreateInventoryButtonLabel>
            {translate(manualInventoryActionKey)}
          </CreateInventoryButtonLabel>
        </CreateInventoryButton>
      </Tooltip>
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["searchSuggest", "handleFilterBy"],
      right: ["handleChangeStatus", "handleCreateInventoryButton", "handleNewRecord"],
    },
    searchSuggest: renderSearchSuggest(),
    handleNewRecord: {
      text: translate("createNewItem"),
      tooltip: translate("createNewItem"),
      action: () => {
        setItemJob({
          number: getNextNumberOfItemsJob(itemsJobs),
          subStatus: ItemJobSubStatus.ACTUAL,
          status: ItemJobStatus.REGISTERED,
          uuid: uuid(),
        });
        setOpenModal(true);
        setMustBeHardwareBox(
          !!currentUser?.company?.autoHardwareBox &&
            itemsJobs.filter((ij) => ij.isHardwareBox)?.length === 0
        );
      },
    },
    handleChangeStatus: renderButtonToChangeStatus(),
    handleFilterBy: {
      categories: [
        {
          name: translate("itemCharacteristics"),
          column: "itemCharacteristics",
          options: enumToArray(ItemCharacteristicsFilter),
        },
        {
          name: translate("status"),
          column: "status",
          options: itemStatus?.map((status) => status?.name || "") || [],
        },
        {
          name: translate("packingType"),
          column: "packingType",
          options: itemsType?.map((type) => type?.name || "") || [],
        },
        {
          name: translate("room"),
          column: "room",
          options: rooms?.map((room) => room?.name || "") || [],
        },
        {
          name: translate("methodOfPreparation"),
          column: "methodOfPreparation",
          options: descriptiveSymbols?.map((method) => method?.name || "") || [],
        },
        {
          name: translate("location"),
          column: "location",
          options: uniqueLocations,
        },
        {
          name: translate("photos"),
          column: "photos",
          options: enumToArray(PhotosFilter),
          multiple: false,
        },
        {
          name: translate("condition"),
          column: "condition",
          options: enumToArray(ConditionFilter),
          multiple: false,
        },
        {
          name: translate("meChecklist"),
          column: "meChecklist",
          options: enumToArray(MEChecklistFilter),
          multiple: false,
        },
      ],
      onChange: handleFilter,
    },
    handleCreateInventoryButton: renderCreateInventoryButton(),
  };

  const deliveredInventoryGeneralActions: GeneralActions = {
    ...generalActions,
    orderBy: {
      left: ["searchSuggest", "handleFilterBy"],
      right: inventoryPdf ? ["handleCreateInventoryButton"] : [],
    },
    handleCreateInventoryButton: renderCreateInventoryButton(),
  };

  useEffect(() => {
    setMustBeHardwareBox(
      !!currentUser?.company.autoHardwareBox && !itemsJobs.some((itemJob) => itemJob.isHardwareBox)
    );
  }, [itemsJobs]);

  return (
    <div data-cy="actual-inventory-list-grid">
      <ShipeeziNewGrid
        data={itemsJobsPaginate.sort(sortItemsJobs)}
        loadingData={loadingData}
        totalItems={calculateTotalItems(itemsJobs, searchAttributes, filters, onFiltersChange)}
        columns={columns}
        generalActions={
          isOnViewMode
            ? inventoryPdf
              ? {
                  orderBy: { right: ["handleCreateInventoryButton"] },
                  handleCreateInventoryButton: renderCreateInventoryButton(),
                }
              : undefined
            : isDeliveredOrder
              ? deliveredInventoryGeneralActions
              : generalActions
        }
        rowActions={
          !inventoryReadOnly
            ? {
                orderBy: ["edit", "copy", "upload"],
              }
            : undefined
        }
        rowActionsExtra={!inventoryReadOnly ? rowActionsExtra : rowActionsExtraView}
        positionActionsColumn="right"
        clearSelection={clearSelection}
        keyExtractor={(itemJob) => itemJob?.id?.toString() || itemJob?.uuid}
        selecteds={selectedItems}
        handleSelect={(selectedItems: string[]) => {
          setSelectedItems(selectedItems);
          setClearSelection(false);
        }}
        emptyData={translate("noInformationToBeShown")}
        pageSize={filters || searchAttributes.length > 0 ? undefined : pageSize}
        currentPage={page}
        pageSizeList={[100, 200, 300]}
        onChangePageSize={setPageSize}
        onNextPage={setPage}
        onPreviousPage={setPage}
        onGoToPage={setPage}
        showSelection={!inventoryReadOnly}
        alwaysShowFooter
      />
      <DrawerComments open={openDrawerComments} onClose={() => setOpenDrawerComments(false)} />
      <DrawerPhotos open={openDrawerPhotos} onClose={() => setOpenDrawerPhotos(false)} />
      <DrawerPDF
        ref={drawerPdfRef}
        file={inventoryPdf?.url || inventoryPdf?.file!}
        filename={inventoryPdf?.file?.name || inventoryPdf?.filename}
      />

      <CreateActualItemPdfModal
        ref={createInventoryModalRef}
        inventoryPdf={inventoryPdf}
        initialManualForm={persistedManualInventoryForm}
        isEditingManualBatch={manualBatchItemKeys.length > 0}
        onPersistManualForm={setPersistedManualInventoryForm}
        onSubmit={onSubmitManualInventoryBatch}
        onSubmitPdfFile={updatePdfFile}
      />

      <ConfirmationModal
        open={openRemoveInventoryPdfConfirm}
        title={translate("removeInventoryPdf")}
        type="warning"
        closeModal={() => setOpenRemoveInventoryPdfConfirm(false)}
        submit={() => {
          updatePdfFile(null);
          setOpenRemoveInventoryPdfConfirm(false);
        }}
        textSubmit={translate("remove")}
        customColorSave={Color.ERROR}
      >
        <span>{translate("removeInventoryPdfConfirmation")}</span>
      </ConfirmationModal>

      {openModal && !isDeliveredOrder && (
        <CreateActualItemModal
          openModal={openModal}
          closeModal={handleCloseModal}
          storageBoxes={storageBoxes}
          orderStatus={orderStatus}
          mustBeHardwareBox={mustBeHardwareBox}
          openOnlyComments={openOnlyComments}
          onSubmit={(itemJob) => {
            onSubmit(itemJob);
            handleCloseModal();
          }}
        />
      )}
      <ChangeItemStatusModal
        open={openChangeStatusModal}
        itemStatus={itemStatus}
        onClose={() => setOpenChangeStatusModal(false)}
        onChange={(status) => {
          if (status && itemsJobs && itemsJobs?.length > 0) {
            const itemsJobsUpdated = [...(itemsJobs || [])];

            itemsJobsUpdated.map((itemJob) => {
              const isSelectedItem = selectedItems?.find(
                (selectedItem) =>
                  selectedItem === itemJob?.id?.toString() || selectedItem === itemJob?.uuid
              );

              if (isSelectedItem) itemJob.status = status;

              return itemJob;
            });
            setClearSelection(true);
            setOpenChangeStatusModal(false);
            update(itemsJobsUpdated);
            NotificationToast.success(translate("statusUpdatedSuccessfully"));
          }
        }}
      />
      <input
        type="file"
        accept=".jpeg,.png,.jpg"
        className={classes.inputFile}
        ref={photoRef}
        onChange={handleFileRead}
      />
    </div>
  );
};

export { ActualInventoryList };
